import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, CalendarDays, Briefcase } from "lucide-react";

/**
 * Slide-up action sheet opened by the central FAB in the bottom navigation.
 * Offers Create Event / Create Service entry points.
 */
const CreateSheet = ({ open, onClose }) => {
    const navigate = useNavigate();

    useEffect(() => {
        if (!open) return;
        const handler = (e) => e.key === "Escape" && onClose();
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, onClose]);

    if (!open) return null;

    const go = (path) => {
        onClose();
        navigate(path);
    };

    return (
        <div
            className="absolute inset-0 z-40 flex items-end"
            data-testid="create-sheet"
            role="dialog"
            aria-modal="true"
        >
            <button
                data-testid="create-sheet-scrim"
                onClick={onClose}
                aria-label="Close"
                className="absolute inset-0 bg-black/40 animate-fade-in"
            />

            <div
                className="relative z-10 w-full bg-card border-t border-border rounded-t-[28px] shadow-[0_-20px_48px_-12px_rgba(0,0,0,0.35)] animate-slide-up pb-[max(20px,env(safe-area-inset-bottom))]"
            >
                <div className="flex justify-center pt-2.5 pb-1">
                    <span className="h-1.5 w-10 rounded-full bg-muted" />
                </div>

                <div className="flex items-center justify-between px-5 pt-2 pb-3">
                    <h2 className="font-display text-[18px] font-semibold">Create</h2>
                    <button
                        data-testid="create-sheet-close"
                        onClick={onClose}
                        className="h-9 w-9 flex items-center justify-center rounded-xl hover:bg-muted"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="px-4 pb-4 space-y-2.5">
                    <CreateOption
                        testId="create-option-event"
                        icon={CalendarDays}
                        title="Create Event"
                        subtitle="Host a gathering — free or paid"
                        onClick={() => go("/create/event")}
                    />
                    <CreateOption
                        testId="create-option-service"
                        icon={Briefcase}
                        title="Create Service"
                        subtitle="Offer what you're great at"
                        onClick={() => go("/create/service")}
                    />
                </div>
            </div>
        </div>
    );
};

const CreateOption = ({ icon: Icon, title, subtitle, onClick, testId }) => (
    <button
        data-testid={testId}
        onClick={onClick}
        className="w-full flex items-center gap-4 p-4 rounded-2xl border border-border bg-background hover:bg-accent/40 active:scale-[0.99] transition-[transform,background-color] duration-200 text-left"
    >
        <span
            className="h-12 w-12 flex items-center justify-center rounded-2xl text-primary"
            style={{
                background:
                    "radial-gradient(120% 120% at 30% 20%, hsl(var(--primary) / 0.22), hsl(var(--primary) / 0.08) 60%, transparent 100%)",
            }}
        >
            <Icon size={22} strokeWidth={2.2} />
        </span>
        <span className="flex-1">
            <span className="block font-display text-[15px] font-semibold">{title}</span>
            <span className="block text-[12.5px] text-muted-foreground mt-0.5">{subtitle}</span>
        </span>
    </button>
);

export default CreateSheet;
